import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { SocialUser } from '../../api/community.types';
import { Button } from '../../ds';
import { Avatar } from './Avatar';
import { useSocial } from './SocialContext';
import { SocialIcon } from './SocialIcon';

const HOVER_OPEN_MS = 250;
const HOVER_CLOSE_MS = 150;

/**
 * Avatar + tên + "level · thời gian". Tác giả khác mình: rê chuột 250ms mở mini profile
 * (Nhắn tin / Xem hồ sơ); trên màn cảm ứng không có hover nên bấm vào tên cũng mở/đóng.
 */
export function AuthorRow({ author, time, size = 40, right }: { author: SocialUser; time?: string; size?: number; right?: ReactNode }) {
  const { me, toast, openDm, openingDm } = useSocial();
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canOpen = !!me && author.id !== me.id;

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  // Bấm ra ngoài thì đóng — bấm bên trong đã stopPropagation nên không tới document.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  const schedule = (next: boolean, delay: number) => {
    if (!canOpen) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(next), delay);
  };
  const hover = { onMouseEnter: () => schedule(true, HOVER_OPEN_MS), onMouseLeave: () => schedule(false, HOVER_CLOSE_MS) };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative', zIndex: open ? 10 : undefined }}>
      <div
        {...hover}
        role={canOpen ? 'button' : undefined}
        tabIndex={canOpen ? 0 : undefined}
        aria-label={canOpen ? `Xem nhanh ${author.name}` : undefined}
        aria-expanded={canOpen ? open : undefined}
        onClick={(e) => {
          if (!canOpen) return;
          e.stopPropagation();
          if (timer.current) clearTimeout(timer.current);
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (canOpen && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, minHeight: Math.max(size, 44), cursor: canOpen ? 'pointer' : 'default' }}
      >
        <Avatar name={author.name} size={size} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--ink)',
              textDecoration: open ? 'underline' : 'none',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {author.name}
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>{time ? `${author.level} · ${time}` : author.level}</div>
        </div>
      </div>
      {right}
      {open && (
        <div
          {...hover}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: Math.max(size, 44) + 6,
            left: 0,
            zIndex: 10,
            width: 260,
            maxWidth: 'calc(100vw - 48px)',
            background: 'var(--surface-page)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-raised)',
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            cursor: 'default',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar name={author.name} size={52} online={author.online} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{author.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{author.online ? `${author.level} · Đang hoạt động` : author.level}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="sm"
              disabled={openingDm}
              style={{ flex: 1, justifyContent: 'center' }}
              icon={<SocialIcon name="chat" size={16} />}
              onClick={() => {
                setOpen(false);
                void openDm(author);
              }}
            >
              Nhắn tin
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setOpen(false);
                toast('Hồ sơ công khai sắp có');
              }}
            >
              Xem hồ sơ
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
